import { useParams, useLocation } from 'react-router';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { usePublicContentCollection } from '../../hooks/api/usePublicContentCollection';
import { Tag, ContentCollection } from '../../hooks/api/useContentCollection';
import { ResourceTile } from '../../components/resource-tile';
import { AudioExperienceTile } from '../../components/audio-experience-tile';
import { VideoExperienceTile } from '../../components/video-experience-tile';
import { DropdownPill } from '../../components/dropdown-pill';
import { PublicCollectionHeader } from '../../components/layout/public-collection-header';
import { Footer } from '../../components/layout/footer';
import { ContentSection } from '../../components/content-section';
import { HeroSection, HeroSectionSkeleton } from '../../components/hero-section';
import { Experience, Content, Resource } from '../../hooks/api/useExperiences';
import { createLoadMoreHandler } from '../../utils/constants';
import { getTopicCircle } from '../../utils/topic-utils';
import { VerticalCollectionImage } from '../../assets/images/svgs';
import { CreateAccountGateModal } from '../../components/modals/create-account-gate-modal/create-account-gate-modal';
import { useTagGroups, TagGroup } from '../../hooks/api/useTagGroups';
import { usePublicEngagementEvent } from '../../hooks/api/usePublicEngagementEvent';
import { useClientDefaultPackage } from '../../hooks/api/useClientDefaultPackage';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import { setSessionStorageItem } from '../../hooks/utils/useSessionStorage';

type ContentType = undefined | 'video' | 'audio' | 'resources';

const LOAD_MORE_VIDEO_ITEMS = 6;
const LOAD_MORE_AUDIO_ITEMS = 4;
const LOAD_MORE_RESOURCE_ITEMS = 4;

const PublicViewCollection = () => {
  const { publicKey } = useParams<{ publicKey: string }>();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryClientId = searchParams.get('client_id');
  const clientIdFromQuery = queryClientId ? parseInt(queryClientId, 10) : undefined;
  const snippetId = searchParams.get('snippet_id') || undefined;
  const engagementSource = snippetId ? 'snippet' : 'direct';

  const {
    data: collectionData,
    isLoading: isLoadingCollection,
    error: collectionError,
  } = usePublicContentCollection(publicKey, clientIdFromQuery);

  const clientIdForPackage =
    clientIdFromQuery && !Number.isNaN(clientIdFromQuery) ? clientIdFromQuery : collectionData?.client_id;
  const { data: defaultPackage } = useClientDefaultPackage(clientIdForPackage);

  useEffect(() => {
    if (!defaultPackage?.code || !defaultPackage.client?.id) return;
    setSessionStorageItem('clientCode', defaultPackage.code);
    setSessionStorageItem('clientId', defaultPackage.client.id.toString());
  }, [defaultPackage]);

  const [accountGateOpen, setAccountGateOpen] = useState(false);
  const openAccountGate = useCallback(() => setAccountGateOpen(true), []);
  const closeAccountGate = useCallback(() => setAccountGateOpen(false), []);
  const { mutate: trackPublicEngagement } = usePublicEngagementEvent();
  const pageViewTracked = useRef(false);
  const dfMdMedia = useMediaQuery('md');

  const trackEngagement = useCallback(
    (
      engagementType:
        | 'collection_page_view'
        | 'collection_video_play'
        | 'collection_audio_play'
        | 'collection_resource_click',
      extraMeta: Record<string, string | number | boolean> = {},
    ) => {
      if (!collectionData?.id) return;
      const clientId = clientIdFromQuery || collectionData.client_id;
      trackPublicEngagement({
        engagementType,
        contentCollectionId: collectionData.id,
        clientId,
        snippetId,
        metaData: {
          source: engagementSource,
          content_collection_id: collectionData.id,
          ...(clientId ? { client_id: clientId } : {}),
          ...extraMeta,
        },
      });
    },
    [
      clientIdFromQuery,
      collectionData?.client_id,
      collectionData?.id,
      engagementSource,
      snippetId,
      trackPublicEngagement,
    ],
  );

  useEffect(() => {
    if (!collectionData?.id || pageViewTracked.current) return;
    pageViewTracked.current = true;
    trackEngagement('collection_page_view');
  }, [collectionData?.id, trackEngagement]);

  const { data: tagGroupsData } = useTagGroups(!!collectionData);
  const [contentTypeFilter, setContentTypeFilter] = useState<ContentType>(undefined);

  const collectionTagGroups = useMemo(() => {
    if (!collectionData?.tags || !tagGroupsData?.length) return [];

    const collectionTagIds = new Set(collectionData.tags.map((tag: Tag) => tag.id));

    const findTagGroupsWithTags = (tagGroups: TagGroup[]): TagGroup[] => {
      const results: TagGroup[] = [];
      for (const tagGroup of tagGroups) {
        if (tagGroup.tags?.some((tag) => collectionTagIds.has(tag.id))) {
          results.push(tagGroup);
        }
        if (tagGroup.children?.length) {
          results.push(...findTagGroupsWithTags(tagGroup.children));
        }
      }
      return results;
    };

    return findTagGroupsWithTags(tagGroupsData);
  }, [collectionData?.tags, tagGroupsData]);

  const primaryTagGroup = useMemo(() => collectionTagGroups[0] || null, [collectionTagGroups]);

  const getInitialItems = useMemo(() => {
    const hasFilter = contentTypeFilter !== undefined;
    return {
      video: hasFilter ? 24 : 6,
      audio: hasFilter ? 16 : 4,
      resources: hasFilter ? 16 : 4,
    };
  }, [contentTypeFilter]);

  const [itemsShown, setItemsShown] = useState({
    videoExperiences: getInitialItems.video,
    audioExperiences: getInitialItems.audio,
    resources: getInitialItems.resources,
  });

  const groupedContent = useMemo(() => {
    if (!collectionData?.items) {
      return {
        listener_audio: [] as Content[],
        client_resources: [] as Resource[],
        global_resources: [] as Resource[],
      };
    }

    const grouped = {
      listener_audio: [] as Content[],
      client_resources: [] as Resource[],
      global_resources: [] as Resource[],
    };

    collectionData.items.forEach((item) => {
      if (item.listener_audio_id && item.data) {
        grouped.listener_audio.push(item.data as Content);
      } else if (item.resource_id && item.data) {
        const resource = item.data as Resource;
        if (resource.client_id) {
          grouped.client_resources.push(resource);
        } else {
          grouped.global_resources.push(resource);
        }
      }
    });

    return grouped;
  }, [collectionData]);

  const videoExperiences = useMemo(
    () => groupedContent.listener_audio.filter((exp) => (exp as Experience | Content).video_id),
    [groupedContent.listener_audio],
  );

  const audioExperiences = useMemo(
    () => groupedContent.listener_audio.filter((exp) => !(exp as Experience | Content).video_id),
    [groupedContent.listener_audio],
  );

  const allResources = useMemo(
    () => [...groupedContent.client_resources, ...groupedContent.global_resources],
    [groupedContent.client_resources, groupedContent.global_resources],
  );

  useEffect(() => {
    setItemsShown({
      videoExperiences: getInitialItems.video,
      audioExperiences: getInitialItems.audio,
      resources: getInitialItems.resources,
    });
  }, [contentTypeFilter, getInitialItems]);

  const loadMoreVideos = createLoadMoreHandler(
    setItemsShown,
    'videoExperiences',
    LOAD_MORE_VIDEO_ITEMS,
    videoExperiences.length,
  );

  const loadMoreAudio = createLoadMoreHandler(
    setItemsShown,
    'audioExperiences',
    LOAD_MORE_AUDIO_ITEMS,
    audioExperiences.length,
  );

  const loadMoreResources = createLoadMoreHandler(
    setItemsShown,
    'resources',
    LOAD_MORE_RESOURCE_ITEMS,
    allResources.length,
  );

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <PublicCollectionHeader onOpenGate={openAccountGate} />
      <div className={`flex-1 ${!dfMdMedia ? 'pt-[76px]' : ''}`}>
        <PublicCollectionBody
          publicKey={publicKey}
          isLoadingCollection={isLoadingCollection}
          collectionError={collectionError}
          collectionData={collectionData}
          primaryTagGroup={primaryTagGroup}
          contentTypeFilter={contentTypeFilter}
          setContentTypeFilter={setContentTypeFilter}
          videoExperiences={videoExperiences}
          audioExperiences={audioExperiences}
          allResources={allResources}
          itemsShown={itemsShown}
          groupedContent={groupedContent}
          loadMoreVideos={loadMoreVideos}
          loadMoreAudio={loadMoreAudio}
          loadMoreResources={loadMoreResources}
          onMemberAction={openAccountGate}
          onTrackEngagement={trackEngagement}
        />
      </div>
      <Footer />
      <CreateAccountGateModal isOpen={accountGateOpen} onClose={closeAccountGate} />
    </div>
  );
};

interface PublicCollectionBodyProps {
  publicKey?: string;
  isLoadingCollection: boolean;
  collectionError: unknown;
  collectionData: ContentCollection | undefined;
  primaryTagGroup: TagGroup | null;
  contentTypeFilter: ContentType;
  setContentTypeFilter: (value: ContentType) => void;
  videoExperiences: (Experience | Content)[];
  audioExperiences: (Experience | Content)[];
  allResources: Resource[];
  itemsShown: {
    videoExperiences: number;
    audioExperiences: number;
    resources: number;
  };
  groupedContent: {
    listener_audio: Content[];
    client_resources: Resource[];
    global_resources: Resource[];
  };
  loadMoreVideos: () => void;
  loadMoreAudio: () => void;
  loadMoreResources: () => void;
  onMemberAction: () => void;
  onTrackEngagement: (
    type: 'collection_video_play' | 'collection_audio_play' | 'collection_resource_click',
    meta?: Record<string, string | number | boolean>,
  ) => void;
}

const PublicCollectionBody = ({
  publicKey,
  isLoadingCollection,
  collectionError,
  collectionData,
  primaryTagGroup,
  contentTypeFilter,
  setContentTypeFilter,
  videoExperiences,
  audioExperiences,
  allResources,
  itemsShown,
  groupedContent,
  loadMoreVideos,
  loadMoreAudio,
  loadMoreResources,
  onMemberAction,
  onTrackEngagement,
}: PublicCollectionBodyProps) => {
  if (!publicKey) {
    return (
      <div className="max-w-page mx-auto px-6 py-8">
        <p>Invalid collection link.</p>
      </div>
    );
  }

  if (!isLoadingCollection && (collectionError || !collectionData)) {
    return (
      <div className="max-w-page mx-auto px-6 py-8">
        <div className="text-center text-red-500">This collection is not available.</div>
      </div>
    );
  }

  const isLoaded = !isLoadingCollection && collectionData;

  return (
    <>
      {isLoaded ? (
        <HeroSection
          title={
            !collectionData!.verticals.length && collectionData!.tags.length > 0
              ? `Featured Content on ${collectionData!.tags[0].name}`
              : collectionData!.name
          }
          description={collectionData!.description}
          icon={
            collectionData!.verticals?.length > 0
              ? VerticalCollectionImage
              : primaryTagGroup
                ? getTopicCircle(primaryTagGroup)
                : undefined
          }
          iconAlt="Collection icon"
          eyebrowText="COLLECTION"
        />
      ) : (
        <HeroSectionSkeleton />
      )}

      <div className="max-w-page mx-auto px-6 py-8">
        <div className="mb-6">
          <DropdownPill
            label="Content type"
            placeholder="All Content"
            options={[
              ...(videoExperiences.length > 0 ? [{ value: 'video', label: 'Video Stories' }] : []),
              ...(audioExperiences.length > 0 ? [{ value: 'audio', label: 'Audio Stories' }] : []),
              ...(allResources.length > 0 ? [{ value: 'resources', label: 'Resources' }] : []),
            ]}
            selectedValue={contentTypeFilter}
            onSelect={(value) => setContentTypeFilter(value as ContentType)}
          />
        </div>

        <ContentSection
          title="Video Stories"
          items={videoExperiences}
          itemsShown={itemsShown.videoExperiences}
          onLoadMore={loadMoreVideos}
          shouldShow={
            (contentTypeFilter === undefined || contentTypeFilter === 'video') &&
            (isLoadingCollection || videoExperiences.length > 0)
          }
          isLoading={isLoadingCollection}
          renderItem={(experience, index) => (
            <VideoExperienceTile
              key={experience?.id || `skeleton-${index}`}
              experience={experience as Experience | Content | undefined}
              handleScheduleModalOpen={onMemberAction}
              allowAnonymousPlayback
              onTileEngage={() =>
                experience?.id && onTrackEngagement('collection_video_play', { experience_id: experience.id })
              }
              isLoading={!experience}
            />
          )}
        />

        <ContentSection
          title="Audio Stories"
          items={audioExperiences}
          itemsShown={itemsShown.audioExperiences}
          onLoadMore={loadMoreAudio}
          shouldShow={
            (contentTypeFilter === undefined || contentTypeFilter === 'audio') &&
            (isLoadingCollection || audioExperiences.length > 0)
          }
          isLoading={isLoadingCollection}
          renderItem={(experience, index) => (
            <AudioExperienceTile
              key={experience?.id || `skeleton-${index}`}
              experience={experience as Experience | Content | undefined}
              handleScheduleModalOpen={onMemberAction}
              allowAnonymousPlayback
              onTileEngage={() =>
                experience?.id && onTrackEngagement('collection_audio_play', { experience_id: experience.id })
              }
              isLoading={!experience}
            />
          )}
        />

        <ContentSection
          title="Resources"
          items={allResources}
          itemsShown={itemsShown.resources}
          onLoadMore={loadMoreResources}
          shouldShow={
            (contentTypeFilter === undefined || contentTypeFilter === 'resources') &&
            (isLoadingCollection || allResources.length > 0)
          }
          isLoading={isLoadingCollection}
          renderItem={(resource, index) => (
            <ResourceTile
              key={resource?.id || `skeleton-${index}`}
              resource={resource || undefined}
              id={resource?.id}
              image={resource?.image_url}
              resourceName={resource?.name}
              caption={resource?.description}
              isGlobal={resource ? !resource.client_id : undefined}
              tagGroupNames={(resource as any)?.tag_group_names}
              isLoading={!resource}
              onTileEngage={() =>
                resource?.id && onTrackEngagement('collection_resource_click', { resource_id: resource.id })
              }
            />
          )}
        />

        {!isLoadingCollection &&
          ((contentTypeFilter === undefined &&
            groupedContent.listener_audio.length === 0 &&
            groupedContent.client_resources.length === 0 &&
            groupedContent.global_resources.length === 0) ||
            (contentTypeFilter === 'video' && videoExperiences.length === 0) ||
            (contentTypeFilter === 'audio' && audioExperiences.length === 0) ||
            (contentTypeFilter === 'resources' && allResources.length === 0)) && (
            <div className="text-center text-gray-500">
              <p className="text-lg">No content found in this collection.</p>
            </div>
          )}
      </div>
    </>
  );
};

export default PublicViewCollection;
